import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

const PriceSettings = () => {
  const [pricePerHour, setPricePerHour] = useState(3.75); // Default $3.75
  const [platformFeeType, setPlatformFeeType] = useState('percentage'); // 'percentage' or 'fixed'
  const [platformFeeValue, setPlatformFeeValue] = useState(10); // 10% default
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const settingsRef = doc(db, 'settings', 'pricing');
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setPricePerHour(data.pricePerHour || 3.75);
        setPlatformFeeType(data.platformFeeType || 'percentage');
        setPlatformFeeValue(data.platformFeeValue || 10);
      } else {
        // Create default settings
        await setDoc(settingsRef, { 
          pricePerHour: 3.75,
          platformFeeType: 'percentage',
          platformFeeValue: 10
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    // Validate price
    const price = parseFloat(pricePerHour);
    if (isNaN(price) || price < 0) {
      setMessage({ type: 'error', text: 'Price must be a positive number' });
      setSaving(false);
      return;
    }
    if (price > 1000) {
      setMessage({ type: 'error', text: 'Price cannot exceed $1000' });
      setSaving(false);
      return;
    }

    // Validate platform fee
    const feeValue = parseFloat(platformFeeValue);
    if (isNaN(feeValue) || feeValue < 0) {
      setMessage({ type: 'error', text: 'Platform fee must be a positive number' });
      setSaving(false);
      return;
    }
    if (platformFeeType === 'percentage' && feeValue > 100) {
      setMessage({ type: 'error', text: 'Percentage fee cannot exceed 100%' });
      setSaving(false);
      return;
    }

    try {
      const settingsRef = doc(db, 'settings', 'pricing');
      await setDoc(settingsRef, { 
        pricePerHour: price,
        platformFeeType,
        platformFeeValue: feeValue
      });
      setMessage({ type: 'success', text: 'Settings updated successfully!' });
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const calculateExamples = () => {
    const price = parseFloat(pricePerHour);
    return [
      { time: '1 hour', price: price },
      { time: '2 hours', price: price * 2 },
      { time: '3 hours', price: price * 3 },
      { time: '4 hours', price: price * 4 },
      { time: '8 hours', price: price * 8 },
    ];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Pricing Settings</h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Price Input */}
          <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Price per Hour (in dollars)
          </label>
          <div className="relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 sm:text-sm">$</span>
            </div>
            <input
              type="number"
              step="0.01"
              min="0"
              max="1000"
              value={pricePerHour}
              onChange={(e) => {
                const value = e.target.value;
                // Allow empty string for editing, otherwise parse to number
                setPricePerHour(value === '' ? '' : parseFloat(value) || 0);
              }}
              className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 pr-3 sm:text-sm border-gray-300 rounded-md py-3 border"
              placeholder="3.75"
            />
          </div>
          <p className="mt-2 text-sm text-gray-500">
            Enter the amount in dollars (e.g., 3.75 for $3.75)
          </p>
          </div>

          {/* Platform Fee Settings */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Platform Fee Type
            </label>
            <select
              value={platformFeeType}
              onChange={(e) => setPlatformFeeType(e.target.value)}
              className="block w-full pl-3 pr-10 py-3 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              <option value="percentage">Percentage of Transaction</option>
              <option value="fixed">Fixed Amount per Transaction</option>
            </select>

            <label className="block text-sm font-medium text-gray-700 mb-2 mt-4">
              Platform Fee Value
            </label>
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">
                  {platformFeeType === 'percentage' ? '%' : '$'}
                </span>
              </div>
              <input
                type="number"
                step={platformFeeType === 'percentage' ? "0.1" : "0.01"}
                min="0"
                max={platformFeeType === 'percentage' ? "100" : "1000"}
                value={platformFeeValue}
                onChange={(e) => setPlatformFeeValue(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 pr-3 sm:text-sm border-gray-300 rounded-md py-3 border"
                placeholder={platformFeeType === 'percentage' ? "10" : "1.00"}
              />
            </div>
            <p className="mt-2 text-sm text-gray-500">
              {platformFeeType === 'percentage' 
                ? `Platform takes ${platformFeeValue}% of each transaction`
                : `Platform takes $${platformFeeValue} from each transaction`}
            </p>
          </div>
        </div>

        {/* Messages */}
        {message.text && (
          <div className={`mt-4 p-4 rounded-md ${
            message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {message.text}
          </div>
        )}

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={saving}
          className={`mt-6 inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white ${
            saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
          } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Pricing Examples */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing Examples</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {calculateExamples().map((example, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600">{example.time}</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                ${example.price.toFixed(2)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20" width="20" height="20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">How it works</h3>
            <div className="mt-2 text-sm text-blue-700 space-y-2">
              <p><strong>For Main Admin Parking:</strong> Customers pay the hourly rate × hours, plus a $1.00 service fee.</p>
              <p><strong>For Partner Parking Lots:</strong> When customers pay at partner lots, the platform automatically takes the configured fee ({platformFeeType === 'percentage' ? `${platformFeeValue}%` : `$${platformFeeValue}`}) from each transaction. Partners receive the remainder after Stripe processing fees.</p>
              <p><strong>Stripe Connect:</strong> Partner parking lot owners must complete Stripe onboarding to receive payments. The platform fee is automatically deducted during payment processing.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceSettings;
